import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Observable, EMPTY, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { WebcastService } from 'rev-portal/scheduledEvents/webcast/Webcast.Service';

import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { SimpleToolbar } from 'rev-shared/htmlEditor/Constants';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { VbScrollToBottomDirective } from 'rev-shared/util/directives/VbScrollToBottom.Directive';

import { ChatDataSource, CHAT_LOAD_THROTTLE_TIME_MS } from './ChatDataSource';
import { IComment } from './Contract';
import styles from './VbWebcastChatSidebar.Component.module.less';

//making sure scroll happens after data load
const SCROLL_TO_BOTTOM_TIME_MS = CHAT_LOAD_THROTTLE_TIME_MS + 20;

@Component({
	selector: 'vb-webcast-chat-sidebar',
	templateUrl: './VbWebcastChatSidebar.Component.html',
	host: {
		'[class]': 'hostClass'
	},
	standalone: false,
	styleUrls: ['../WebcastSidebar.ng.less']
})
export class VbWebcastChatSidebarComponent implements OnInit, OnDestroy {
	@Input() public webcast: WebcastModel;
	@Input() public legacyStyle: boolean;

	@ViewChild(VbScrollToBottomDirective) public scrollHandler: VbScrollToBottomDirective;

	public get hostClass(): string {
		return this.legacyStyle ? `${styles.root} ${styles.legacyStyle}` : styles.root;
	}

	public user = this.UserContext.getUser();
	private pendingCommands = {};
	public status: { [key: string]: boolean };
	private readonly unsubscribePushHandler: Subscription = new Subscription();
	public readonly chatSource = new ChatDataSource();
	public readonly editorConfig = {
		toolbar: SimpleToolbar,
		height: 80
	};

	public readonly styles = styles;

	public commentText: string = '';
	public isEditorLoaded: boolean;
	public isEventAdminOrModerator: boolean;
	public blockedUsers: { chatAuthorBlocked: boolean, [key: string]: boolean } = { chatAuthorBlocked: false };
	private isComposeSessionActive = false;
	private wasNearBottomAtComposeStart = true;

	constructor(
		private readonly DateParsers: DateParsersService,
		private readonly PushBus: PushBus,
		private readonly UserContext: UserContextService,
		private readonly WebcastService: WebcastService
	) {}

	public ngOnInit(): void {
		this.isEventAdminOrModerator = this.webcast.currentUser.isEventAdminOrModerator;
		this.unsubscribePushHandler.add(
			this.WebcastService.chatBlockedUsers$.subscribe(blocked => {
				this.blockedUsers = blocked;
			})
		);
		this.loadData();
	}

	public ngOnDestroy(): void {
		this.unsubscribePushHandler?.unsubscribe();
	}

	public postComment(): void {
		if(!this.commentText || this.blockedUsers.chatAuthorBlocked) {
			return;
		}
		const wasNearBottom = this.isComposeSessionActive
			? this.wasNearBottomAtComposeStart
			: this.scrollHandler.isScrollNearBottom();
		const comment: IComment = {
			userId: this.user.id,
			comment: this.commentText,
			isAuthoredByCurrentUser: true,
			isAdminOrModerator: this.isEventAdminOrModerator
		};

		this.WebcastService
			.addWebcastComment(this.webcast, comment)
			.then(commandId => this.pendingCommands[commandId] = comment);

		this.displayPendingComment(comment);
		this.commentText = '';
		this.isComposeSessionActive = false;
		this.wasNearBottomAtComposeStart = this.scrollHandler.isScrollNearBottom();

		if (wasNearBottom) {
			this.scrollBottom();
		}
	}

	public onCommentInput(): void {
		if (!this.commentText) {
			this.isComposeSessionActive = false;
			return;
		}

		if (!this.isComposeSessionActive) {
			this.wasNearBottomAtComposeStart = this.scrollHandler.isScrollNearBottom();
			this.isComposeSessionActive = true;
		}
	}

	private loadData(): void {
		this.status = { loading: true };

		this.loadComments()
			.then(() => {
				setTimeout(() => {
					this.status = { active: true };
					this.scrollBottom();
				}, 500);
			})
			.catch(() => this.status = { error: true });
	}

	private loadComments(): Promise<void> {
		return this.WebcastService.getWebcastComments(this.webcast)
			.then(comments => {
				const updatedComments = this.shapeComments(comments);

				this.chatSource.setComments(updatedComments);
				this.unsubscribePushHandler.add(this.initPushHandlers());
			});
	}

	private displayPendingComment(comment: IComment) {
		comment.name = this.user.fullName;
		comment.userProfileImageUri = this.user.profileImageUri;
		this.chatSource.append(comment);
	}

	private shapeComments(comments: IComment[]): IComment[] {
		return comments.map(comment => {
			const isAuthoredByCurrentUser = comment.userId === this.user.id;
			if (comment.hidden && !isAuthoredByCurrentUser && !this.isEventAdminOrModerator) {
				return;
			}
			this.updateCommentData(comment, { isAuthoredByCurrentUser });
			this.updateUserName(comment);
			return comment;
		}).filter(Boolean);
	}

	public updateUserName(comment: IComment): void {
		comment.name = [comment.userFirstName, comment.userLastName].filter(Boolean).join(' ');
	}

	public addCommentToWebcast(msgData): void {
		if (!msgData) {
			return;
		}

		const wasNearBottom = this.scrollHandler.isScrollNearBottom();
		const date = this.DateParsers.parseUTCDate(msgData.date);
		const pendingComment = this.pendingCommands[msgData.commandId];

		if(pendingComment){
			pendingComment.date = date;
			pendingComment.id = msgData.id;
			delete this.pendingCommands[msgData.commandId];
		} else{
			const comment = msgData;
			this.updateUserName(comment);
			this.chatSource.append(comment);
		}
		if (wasNearBottom) {
			this.scrollBottom();
		}
	}

	public updateCommentStatus(comment: IComment): void {
		this.chatSource.update(comment);
	}

	public handleUserReactions(msgData): void {
		if (!msgData) {
			return;
		}

		console.info(msgData);
	}


	public initPushHandlers(): Subscription {
		const adminSub = this.webcast.currentUser.isEventAdmin$
			.pipe(
				switchMap(isAdmin => {
					return !isAdmin
						? EMPTY
						: new Observable(() =>
							this.PushBus.subscribe(this.user.id, {
								WebcastCommentAdded: (comment: IComment) => {
									if (this.webcast.id === comment.webcastId) {
										this.updateCommentData(comment, { isAuthoredByCurrentUser: comment.userId === this.user.id });
										this.addCommentToWebcast(comment);
									}
								}
							})
						);
				})
			).subscribe();

		const webcastPushSub = this.PushBus.subscribe(this.webcast.id, {
			WebcastCommentAdded: (comment: any) => {
				this.updateCommentData(comment, { isAuthoredByCurrentUser: comment.userId === this.user.id });
				this.addCommentToWebcast(comment);
			},

			WebcastCommentStatusUpdated: (comment: IComment) => {
				if (this.webcast.id === comment.webcastId) {
					this.updateCommentData(comment, { isAuthoredByCurrentUser: comment.userId === this.user.id, hidden: comment.hidden, statusUpdateInProgress: false });
					this.updateCommentStatus(comment);
				}
			}
		});

		const webcastReactionsPushSub = this.PushBus.subscribe(this.webcast.id, {
			UserReactionsPublished: (data: any) => this.handleUserReactions(data)
		});

		adminSub.add(webcastPushSub);
		adminSub.add(webcastReactionsPushSub);
		return adminSub;
	}

	private updateCommentData(existingComment: IComment, newComment: any): void {
		Object.assign(existingComment, newComment);
	}

	private scrollBottom(): void {
		setTimeout(() => this.scrollHandler.scrollToBottom(), SCROLL_TO_BOTTOM_TIME_MS);
	}

	public onChangeCommentDisplay(comment: IComment): void {
		comment.statusUpdateInProgress = true;
		this.WebcastService.updateCommentStatus(this.webcast, comment);
	}

	public onDisableAttendeeToComment(comment: IComment, disable: boolean): void {
		comment.statusUpdateInProgress = true;
		this.WebcastService.disableAttendeeToComment(this.webcast, comment.userId, disable)
			.finally(() => comment.statusUpdateInProgress = false);
	}

}
